import arrayLength from "@-/com.npmjs.extra-array__length";
import useLatest from "@-/com.npmjs.use-latest";
import { useDeviceSetting } from "@-/dev.improve.haniverse.lib.device-settings";
import type { MorphemeDetailsTileSlice } from "@-/dev.improve.haniverse.lib.morpheme.entities";
import { isCardCompleted } from "@-/dev.improve.haniverse.lib.morpheme.grapheme.file.completion";
import { useCallback, useMemo, useState } from "react";
import {
  DECK_SECTION_PAGE_SIZE,
  useDeckCardDetailsInfiniteQuery,
} from "#use-deck-card-details-infinite-query";

const EMPTY_FRONT_SET: ReadonlySet<string> = new Set();

/**
 * The cards one deck section renders, revealed incrementally.
 *
 * Two independent things grow together here:
 * - the REVEALED window (`revealMore`, wired to the section's `onEndReached`): how many of the
 *   section's fronts are rendered at all. Revealing is instant — every card paints from its
 *   actor-only core slice — so a slow scroll never sees the section run out.
 * - the DETAILS (content/static DB) for the revealed window, paged in by
 *   `useDeckCardDetailsInfiniteQuery`. A card renders provisionally until its page lands
 *   (`pendingDetailsFronts`), then re-renders with the real slice.
 *
 * `hasMore` is the "more below" footer signal: fronts not yet revealed, or — under the
 * hide-completed authoring setting, where a card cannot be shown until its completion is known
 * — revealed fronts whose details are still loading.
 *
 * `fronts` must already be ordered and de-duplicated. The revealed window is kept across sort /
 * refetch changes (a returning tab must not snap the user's scroll position back to the top);
 * it is only clamped to the current front count.
 */
export function useDeckSectionCards({
  coreSliceByFront,
  enabled = true,
  fronts,
}: {
  coreSliceByFront: Pick<ReadonlyMap<string, MorphemeDetailsTileSlice>, "get">;
  /** Gate the details fetch (e.g. a collapsed section) — reveal state is kept either way. */
  enabled?: boolean;
  fronts: readonly string[];
}) {
  const [hideCompletedAuthoringCardsEnabled] = useDeviceSetting(
    "hideCompletedAuthoringCardsEnabled",
  );
  const [revealedCount, setRevealedCount] = useState(DECK_SECTION_PAGE_SIZE);
  const totalCount = arrayLength(fronts);
  const visibleCount = Math.min(revealedCount, totalCount);
  const visibleFronts = useMemo(() => fronts.slice(0, visibleCount), [fronts, visibleCount]);

  const { detailsByFront } = useDeckCardDetailsInfiniteQuery({
    enabled,
    fronts,
    requiredCount: visibleCount,
  });

  const pendingDetailsFronts = useMemo(() => {
    const pending = new Set(visibleFronts.filter((front) => !detailsByFront.has(front)));
    return pending.size === 0 ? EMPTY_FRONT_SET : pending;
  }, [detailsByFront, visibleFronts]);

  const cards = useMemo(
    () =>
      visibleFronts.flatMap((front): MorphemeDetailsTileSlice[] => {
        const details = detailsByFront.get(front);
        // Completion is a content-DB fact: under the hide-completed authoring setting a card is
        // withheld until its details say it is NOT completed.
        if (
          hideCompletedAuthoringCardsEnabled &&
          (details === undefined || (details !== null && isCardCompleted(details)))
        ) {
          return [];
        }
        const slice = details ?? coreSliceByFront.get(front);
        return slice == null ? [] : [slice];
      }),
    [coreSliceByFront, detailsByFront, hideCompletedAuthoringCardsEnabled, visibleFronts],
  );

  const totalCountRef = useLatest(totalCount);
  const revealMore = useCallback(() => {
    setRevealedCount((count) =>
      count >= totalCountRef.current ? count : count + DECK_SECTION_PAGE_SIZE,
    );
  }, [totalCountRef]);

  const hasMore =
    visibleCount < totalCount ||
    (hideCompletedAuthoringCardsEnabled && pendingDetailsFronts.size > 0);

  return { cards, hasMore, pendingDetailsFronts, revealMore };
}
